import { Switch } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { ImuDataBlock } from '../types';
import './imugraph.scss';

/* -------------------------------------------------------------------------------- *\
|                               Constantes
\* -------------------------------------------------------------------------------- */
const c_fontsize_label: number = 10;
const c_margin_x: number = 10;
const c_margin_y: number = 10;
const yaxismargin = 5;
const CONTEXT_RATIO = 0.1; // 10%

/* -------------------------------------------------------------------------------- *\
|                               Vairables
\* -------------------------------------------------------------------------------- */
var g_xfirst: number = 10;
var g_xpcarr: number = 0;
var g_yaxistop: number = 20;
var g_yaxisbottom: number = 0;

/* -------------------------------------------------------------------------------- *\
|                               SpeedsProps
\* -------------------------------------------------------------------------------- */
interface ImuGraphProps
{
	block: ImuDataBlock | null,
	prevBlock?: ImuDataBlock | null,
	nextBlock?: ImuDataBlock | null,
	children?: Node
}
/* -------------------------------------------------------------------------------- *\
|                               ImuGraph
\* -------------------------------------------------------------------------------- */
export const ImuGraph : FC<ImuGraphProps> = ({block, prevBlock, nextBlock}) =>
{
    const refcontainer = useRef<HTMLDivElement>(null);
    const refcanvas = useRef<HTMLCanvasElement>(null);

    const [width, setWidth] = useState<number>(300);
    const [height, setHeight] = useState<number>(300);
    const [showContext, setShowContext] = useState<boolean>(false);

    /* -------------------------------------------------------------------------------- *\
    |                               drawGraph
    \* -------------------------------------------------------------------------------- */
    function drawGraph(ctx: CanvasRenderingContext2D)
    {
        g_xfirst = c_margin_x;
        g_xpcarr = width - c_margin_x;
        g_yaxistop = c_margin_y;
        g_yaxisbottom = height - c_margin_y;
        const gheight = g_yaxisbottom - g_yaxistop;

        var speedytop = g_yaxistop;
        var speedheight = gheight;
        var axisy = height / 2;

        ctx.fillStyle = "black";
        ctx.beginPath();
        ctx.rect(0, 0, width, height);
        ctx.fill();

        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;

        ctx.beginPath();
        ctx.moveTo(g_xfirst, g_yaxistop);
        ctx.lineTo(g_xfirst, g_yaxisbottom + yaxismargin);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(g_xpcarr, g_yaxistop);
        ctx.lineTo(g_xpcarr, g_yaxisbottom + yaxismargin);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(g_xfirst - 5, axisy);
        ctx.lineTo(g_xpcarr + 2, axisy);
        ctx.stroke();

        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.font = `${c_fontsize_label}px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif`;

        const colors = ["00ff00", "55ff55", "aaffaa", "0000ff", "5555ff", "aaaaff"];

        // Build segments: [samples, alpha, isContext]
        const currentSamples = block?.samples ?? [];
        const prevCount = showContext && prevBlock ? Math.ceil(prevBlock.samples.length * CONTEXT_RATIO) : 0;
        const nextCount = showContext && nextBlock ? Math.ceil(nextBlock.samples.length * CONTEXT_RATIO) : 0;
        const prevSamples = prevBlock ? prevBlock.samples.slice(prevBlock.samples.length - prevCount) : [];
        const nextSamples = nextBlock ? nextBlock.samples.slice(0, nextCount) : [];

        const totalCount = prevSamples.length + currentSamples.length + nextSamples.length;
        if (totalCount === 0) return;

        // Compute global min/max across all visible samples
        const allSamples = [...prevSamples, ...currentSamples, ...nextSamples];
        let speedmin = 32767;
        let speedmax = -32767;
        for (let field = 0; field < 6; field++)
        {
            for (const s of allSamples)
            {
                let o = 0;
                switch (field)
                {
                    case 0: o = s.gyroX; break;
                    case 1: o = s.gyroY; break;
                    case 2: o = s.gyroZ; break;
                    case 3: o = s.xlX; break;
                    case 4: o = s.xlY; break;
                    case 5: o = s.xlZ; break;
                }
                speedmin = Math.min(o, speedmin);
                speedmax = Math.max(o, speedmax);
            }
        }
        speedmin = Math.max(-32767, speedmin - 1000);
        speedmax = Math.min(32767, speedmax + 1000);
        const m = Math.max(speedmax, -speedmin);
        speedmin = -m;
        speedmax = m;

        // Draw separator lines for context boundaries
        if (showContext && prevSamples.length > 0)
        {
            const sepX = g_xfirst + prevSamples.length * (g_xpcarr - g_xfirst) / (totalCount - 1);
            ctx.save();
            ctx.strokeStyle = 'rgba(255,255,255,0.3)';
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo(sepX, g_yaxistop);
            ctx.lineTo(sepX, g_yaxisbottom);
            ctx.stroke();
            ctx.restore();
        }
        if (showContext && nextSamples.length > 0)
        {
            const sepX = g_xfirst + (prevSamples.length + currentSamples.length - 1) * (g_xpcarr - g_xfirst) / (totalCount - 1);
            ctx.save();
            ctx.strokeStyle = 'rgba(255,255,255,0.3)';
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo(sepX, g_yaxistop);
            ctx.lineTo(sepX, g_yaxisbottom);
            ctx.stroke();
            ctx.restore();
        }

        // Draw each field in 3 passes: prev (dim), current (full), next (dim)
        const segments: { samples: typeof allSamples, offset: number, alpha: number }[] = [
            { samples: prevSamples,    offset: 0,                                    alpha: 1.0 },
            { samples: currentSamples, offset: prevSamples.length,                   alpha: 1.0 },
            { samples: nextSamples,    offset: prevSamples.length + currentSamples.length, alpha: 1.0 },
        ];

        for (let field = 0; field < 6; field++)
        {
            for (const seg of segments)
            {
                if (seg.samples.length === 0) continue;
                ctx.save();
                ctx.globalAlpha = seg.alpha;
                ctx.strokeStyle = '#' + colors[field];
                ctx.beginPath();
                for (let i = 0; i < seg.samples.length; i++)
                {
                    let o = 0;
                    switch (field)
                    {
                        case 0: o = seg.samples[i].gyroX; break;
                        case 1: o = seg.samples[i].gyroY; break;
                        case 2: o = seg.samples[i].gyroZ; break;
                        case 3: o = seg.samples[i].xlX; break;
                        case 4: o = seg.samples[i].xlY; break;
                        case 5: o = seg.samples[i].xlZ; break;
                    }
                    const globalIndex = seg.offset + i;
                    const x = g_xfirst + globalIndex * (g_xpcarr - g_xfirst) / (totalCount - 1);
                    const y = speedytop + speedheight - ((o - speedmin) / (speedmax - speedmin)) * speedheight;
                    if (i === 0 && seg.offset === 0) ctx.moveTo(x, y);
                    else if (i === 0)
                    {
                        // connect to last point of previous segment visually
                        ctx.moveTo(x, y);
                    }
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
                ctx.restore();
            }
        }
    }
    useEffect(() =>
    {
        handleResize();
        window.addEventListener('resize', handleResize);

        return () =>
        {
            window.removeEventListener('resize', handleResize);
        }
        // eslint-disable-next-line
    }, [block]);

    useEffect(() =>
    {
        const canvas = refcanvas.current;
        if (canvas)
        {
            const context = canvas.getContext('2d');
            if (context)
            {
                drawGraph(context);
            }
        }
        // eslint-disable-next-line
    }, [width, height, block, prevBlock, nextBlock, showContext]);

    const handleResize = () =>
    {
        if (refcontainer && refcontainer.current && width !== refcontainer.current.clientWidth)
        {
            setWidth(refcontainer.current.clientWidth);
        }
        if (refcontainer && refcontainer.current && height !== refcontainer.current.clientHeight)
        {
            setHeight(refcontainer.current.clientHeight);
        }
    }

    return (
        <div className='imugraph'>
            <div className='imugraph__datas'>
                <div className='imugraph__datas__graph' ref={refcontainer}>
                    <canvas ref={refcanvas} width={width} height={height}
                    onContextMenu={e => e.preventDefault()} />
                    <label className='imugraph__datas__graph__overlay'>
                        <Switch size="small" checked={showContext} onChange={setShowContext} />
                        &nbsp;±10%
                    </label>
                </div>
            </div>
        </div>
    );
}
